import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { BRAND, isPlaceholderBrand } from '@/brand/brand';

/**
 * Shouts when the build is still wearing the placeholder identity.
 *
 * A generated app that ships the default palette looks like every other generated app. Silence would
 * let that pass review; this makes the omission impossible to miss and says what to do about it.
 *
 * Two rules it has to satisfy at once, which is why it is a compact bar that expands rather than a
 * panel:
 *  - It must be seen. It shows in PRODUCTION builds too, because the preview everyone reviews IS a
 *    production build — hiding it there hides it from the only people who can act on it.
 *  - It must never block the product. It sits BELOW sheets (z-30 against the sheet's z-50), above
 *    the tab bar, and is one line until asked, so it can never cover a control someone needs.
 *
 * It renders nothing once `BRAND.applied` is true, so the fix is to do the work, not delete the file.
 */
export function BrandGuard() {
	const [expanded, setExpanded] = useState(false);
	const [dismissed, setDismissed] = useState(false);

	if (!isPlaceholderBrand() || dismissed) return null;

	return (
		<div
			role="alert"
			className="pointer-events-auto fixed inset-x-2 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-30 mx-auto max-w-[520px] overflow-hidden rounded-xl border text-left shadow-lg backdrop-blur"
			style={{
				borderColor: 'rgba(255,196,84,0.4)',
				background: 'rgba(38,26,12,0.94)',
				color: '#ffe6b8',
			}}
		>
			<div className="flex items-center gap-2.5 px-3 py-2.5">
				<AlertTriangle className="size-4 shrink-0" style={{ color: '#ffc454' }} />
				<p className="min-w-0 flex-1 truncate text-[13px] font-medium">
					No brand applied — placeholder identity
				</p>
				<button
					onClick={() => setExpanded((v) => !v)}
					className="shrink-0 rounded-md px-2 py-1 text-[12px] font-medium underline underline-offset-2 opacity-80 transition-opacity hover:opacity-100"
					aria-expanded={expanded}
				>
					{expanded ? 'Hide' : 'Why'}
				</button>
				<button
					onClick={() => setDismissed(true)}
					aria-label="Dismiss brand warning"
					className="-mr-1 shrink-0 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100"
				>
					<X className="size-4" />
				</button>
			</div>

			{expanded && (
				<div className="border-t px-3 py-2.5 text-[12.5px] leading-relaxed" style={{ borderColor: 'rgba(255,196,84,0.2)' }}>
					<p className="opacity-85">
						Every colour on screen comes from the default palette in{' '}
						<code className="rounded bg-black/30 px-1 py-0.5">src/brand/brand.ts</code>, so this build
						looks like every other generated app. The assigned brand language — colours, type, radii,
						voice — is what makes it look designed rather than produced.
					</p>
					<p className="mt-2 opacity-85">
						Fill in that file, then set <code className="rounded bg-black/30 px-1 py-0.5">applied: true</code>.
						The splash, tab bar, sheets and every control read from it, so the app re-skins at once.
					</p>
					<p className="mt-2 opacity-55">
						Currently: {BRAND.name} — {BRAND.tagline}
					</p>
				</div>
			)}
		</div>
	);
}
